import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Shared UI primitives.
 *
 * These exist because native browser affordances — `alert`, `confirm`, an
 * unstyled "Loading…" — are the fastest way to make a product look unfinished.
 * They also block the main thread and cannot be styled or made accessible.
 */

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

interface IconProps {
  size?: number;
}

function svg(path: JSX.Element, { size = 20 }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

export const TrunkIcon = (p: IconProps): JSX.Element =>
  svg(
    <>
      <rect x="3" y="4" width="18" height="6" rx="2" />
      <rect x="3" y="14" width="18" height="6" rx="2" />
      <path d="M7 7h.01M7 17h.01" />
    </>,
    p,
  );

export const NumberIcon = (p: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </>,
    p,
  );

export const UserIcon = (p: IconProps): JSX.Element =>
  svg(
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20v-1.5A4.5 4.5 0 0 1 9.5 14h5a4.5 4.5 0 0 1 4.5 4.5V20" />
    </>,
    p,
  );

export const CallIcon = (p: IconProps): JSX.Element =>
  svg(
    <path d="M6.6 3.5h-2A1.6 1.6 0 0 0 3 5.2 16.8 16.8 0 0 0 18.8 21a1.6 1.6 0 0 0 1.7-1.6v-2a1.6 1.6 0 0 0-1.4-1.6 10 10 0 0 1-2.2-.5 1.6 1.6 0 0 0-1.7.4l-.8.8a13.4 13.4 0 0 1-5-5l.8-.8a1.6 1.6 0 0 0 .4-1.7 10 10 0 0 1-.5-2.2 1.6 1.6 0 0 0-1.5-1.3z" />,
    p,
  );

export const VoicemailIcon = (p: IconProps): JSX.Element =>
  svg(
    <>
      <circle cx="6.5" cy="13" r="3.8" />
      <circle cx="17.5" cy="13" r="3.8" />
      <path d="M6.5 16.8h11" />
    </>,
    p,
  );

export const LogoMark = ({ size = 24 }: { size?: number }): JSX.Element => (
  <img width={size} height={size} src={`${import.meta.env.BASE_URL}brand/favicon.svg`} alt="" aria-hidden="true" />
);

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------

/**
 * Wordmark. Deliberately generic until the product has a real name — swap
 * BRAND_NAME and this is rebranded everywhere it appears.
 */
export const BRAND_NAME = 'Schwemm Handels';

export function Brand({ sub }: { sub?: string }): JSX.Element {
  return (
    <div className="brand">
      <img className="schwemm-logo" src={`${import.meta.env.BASE_URL}brand/schwemm-logo-dark.svg`} alt="Schwemm Handels" />
      {sub && <span className="brand-sub">{sub}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

export interface Toast {
  id: number;
  title: string;
  text?: string;
  tone?: 'ok' | 'bad' | 'info';
  /** Rendered monospaced and select-all — for generated passwords. */
  mono?: boolean;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>) => void;
  /** Convenience for the common "operation failed" path. */
  error: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast used outside ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { ...toast, id }]);

    // Anything containing a credential stays until dismissed — auto-hiding a
    // one-time password would lose it for good.
    if (!toast.mono) {
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
    }
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      push,
      error: (text: string) => push({ title: 'Something went wrong', text, tone: 'bad' }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone ?? 'info'}`}>
            <span className="toast-accent" />
            <div className="toast-body">
              <div className="toast-title">{t.title}</div>
              {t.text && (
                <div className={t.mono ? 'toast-text mono' : 'toast-text'}>{t.text}</div>
              )}
            </div>
            <button
              type="button"
              className="toast-close"
              onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------

interface ConfirmProps {
  title: string;
  body: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmProps): JSX.Element {
  return (
    <div
      className="dialog-backdrop"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      role="presentation"
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        <p>{body}</p>
        <div className="dialog-actions">
          <button type="button" className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={destructive ? 'danger' : ''}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty + loading states
// ---------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: JSX.Element;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}): JSX.Element {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <span className="empty-title">{title}</span>
      <p className="empty-body">{body}</p>
      {action && (
        <button type="button" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Placeholder rows matching the shape of the table that is loading. */
export function SkeletonTable({
  rows = 4,
  columns = [30, 20, 18, 14],
}: {
  rows?: number;
  columns?: number[];
}): JSX.Element {
  return (
    <div className="skeleton-table" aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r}>
          {columns.map((width, c) => (
            <span
              className="skeleton-bar"
              key={c}
              style={{
                width: `${width}%`,
                // Staggered so the shimmer reads as one surface, not a grid.
                animationDelay: `${(r * columns.length + c) * 0.05}s`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
